import React from "react";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  OutlinedInput,
  TextField
} from "@material-ui/core";
import { useIntl } from "react-intl";
import NationalIdNumber from "../../components/InputMasks/NationalIdNumberMask";
import PhoneMask from "../../components/InputMasks/PhoneMask";
import FormTitle from "./FormTitle";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      "& > *": {
        margin: theme.spacing(1)
      }
    },
    formSection: {
      marginBottom: theme.spacing(3)
    },
    formFieldGrid: {
      marginBottom: theme.spacing(2)
    },
    menuPaper: {
      maxHeight: theme.spacing(48)
    }
  })
);

interface IProps {
  formik: any;
}

function ContactInformationForm(props: IProps) {
  const classes = useStyles();
  const intl = useIntl();

  const year = new Date().getFullYear() - 18;
  const years = Array.from(new Array(75), (_val, index) => year - index);
  const educationLevels = [
    intl.formatMessage({ id: "EDUCATION_FIRST_LEVEL" }),
    intl.formatMessage({ id: "EDUCATION_SECOND_LEVEL" }),
    intl.formatMessage({ id: "EDUCATION_THIRD_LEVEL" }),
    intl.formatMessage({ id: "EDUCATION_FORTH_LEVEL" })
  ];

  return (
    <React.Fragment>
      <FormTitle title={intl.formatMessage({ id: "CONTACT_INFORMATION_TITLE" })} />
      <Grid className={classes.formSection} container spacing={3}>
        <Grid className={classes.formFieldGrid} item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            variant="outlined"
            name="FullName"
            label={intl.formatMessage({ id: "FULL_NAME_LABEL" })}
            autoComplete="name"
            value={props.formik.values.FullName}
            onBlur={props.formik.handleBlur}
            onChange={props.formik.handleChange}
            error={props.formik.touched.FullName && Boolean(props.formik.errors.FullName)}
            helperText={props.formik.touched.FullName && props.formik.errors.FullName}
          />
        </Grid>
        <Grid className={classes.formFieldGrid} item xs={12} sm={6} md={4}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel
              id="date-select-outlined-label"
              error={props.formik.touched.BirthDate && Boolean(props.formik.errors.BirthDate)}
            >
              {intl.formatMessage({ id: "BIRTH_DATE_LABEL" })}
            </InputLabel>
            <Select
              labelId="date-select-outlined-label"
              label={intl.formatMessage({ id: "BIRTH_DATE_LABEL" })}
              name="BirthDate"
              value={props.formik.values.BirthDate}
              onBlur={props.formik.handleBlur}
              onChange={props.formik.handleChange}
              error={props.formik.touched.BirthDate && Boolean(props.formik.errors.BirthDate)}
              MenuProps={{ classes: { paper: classes.menuPaper } }}
            >
              {years.map((year, index) => {
                return (
                  <MenuItem key={index} value={year}>
                    {year}
                  </MenuItem>
                );
              })}
            </Select>
            <FormHelperText
              error={props.formik.touched.BirthDate && Boolean(props.formik.errors.BirthDate)}
            >
              {props.formik.touched.BirthDate && props.formik.errors.BirthDate}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid className={classes.formFieldGrid} item xs={12} sm={6} md={4}>
          <FormControl fullWidth variant="outlined">
            <InputLabel
              id="national_id_number-outlined-label"
              error={
                props.formik.touched.NationalIdNumber &&
                Boolean(props.formik.errors.NationalIdNumber)
              }
            >
              {intl.formatMessage({ id: "TCKN_LABEL" })}
            </InputLabel>
            <OutlinedInput
              id="national_id_number"
              label={intl.formatMessage({ id: "TCKN_LABEL" })}
              name="NationalIdNumber"
              value={props.formik.values.NationalIdNumber}
              onBlur={props.formik.handleBlur}
              onChange={props.formik.handleChange}
              inputComponent={NationalIdNumber as any}
              error={
                props.formik.touched.NationalIdNumber &&
                Boolean(props.formik.errors.NationalIdNumber)
              }
            />
            <FormHelperText
              error={
                props.formik.touched.NationalIdNumber &&
                Boolean(props.formik.errors.NationalIdNumber)
              }
            >
              {props.formik.touched.NationalIdNumber && props.formik.errors.NationalIdNumber}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid className={classes.formFieldGrid} item xs={12} sm={6} md={4}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel
              id="education-select-outlined-label"
              error={
                props.formik.touched.EducationLevel && Boolean(props.formik.errors.EducationLevel)
              }
            >
              {intl.formatMessage({ id: "EDUCATION_LEVEL_LABEL" })}
            </InputLabel>
            <Select
              labelId="education-select-outlined-label"
              label={intl.formatMessage({ id: "EDUCATION_LEVEL_LABEL" })}
              name="EducationLevel"
              value={props.formik.values.EducationLevel}
              onBlur={props.formik.handleBlur}
              onChange={props.formik.handleChange}
              error={
                props.formik.touched.EducationLevel && Boolean(props.formik.errors.EducationLevel)
              }
              MenuProps={{ classes: { paper: classes.menuPaper } }}
            >
              {educationLevels.map((value, index) => {
                return (
                  <MenuItem key={index} value={value}>
                    {value}
                  </MenuItem>
                );
              })}
            </Select>
            <FormHelperText
              error={
                props.formik.touched.EducationLevel && Boolean(props.formik.errors.EducationLevel)
              }
            >
              {props.formik.touched.EducationLevel && props.formik.errors.EducationLevel}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid className={classes.formFieldGrid} item xs={12} sm={6} md={4}>
          <FormControl fullWidth variant="outlined">
            <InputLabel
              id="formatted-phone-mask-input_label"
              error={props.formik.touched.Phone && Boolean(props.formik.errors.Phone)}
            >
              {intl.formatMessage({ id: "PHONE_LABEL" })}
            </InputLabel>
            <OutlinedInput
              type="tel"
              label={intl.formatMessage({ id: "PHONE_LABEL" })}
              name="Phone"
              value={props.formik.values.Phone}
              onBlur={props.formik.handleBlur}
              onChange={props.formik.handleChange}
              inputComponent={PhoneMask as any}
              error={props.formik.touched.Phone && Boolean(props.formik.errors.Phone)}
            />
            <FormHelperText
              error={props.formik.touched.Phone && Boolean(props.formik.errors.Phone)}
            >
              {props.formik.touched.Phone && props.formik.errors.Phone}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid className={classes.formFieldGrid} item xs={12} sm={6} md={4}>
          <TextField
            variant="outlined"
            fullWidth
            label={intl.formatMessage({ id: "EMAIL_LABEL" })}
            autoComplete="email"
            name="Email"
            value={props.formik.values.Email}
            onBlur={props.formik.handleBlur}
            onChange={props.formik.handleChange}
            error={props.formik.touched.Email && Boolean(props.formik.errors.Email)}
            helperText={props.formik.touched.Email && props.formik.errors.Email}
          />
        </Grid>
        <Grid className={classes.formFieldGrid} item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            variant="outlined"
            name="ResidenceCity"
            label={intl.formatMessage({ id: "RESIDENCE_CITY_LABEL" })}
            autoComplete="name"
            value={props.formik.values.ResidenceCity}
            onBlur={props.formik.handleBlur}
            onChange={props.formik.handleChange}
            error={props.formik.touched.ResidenceCity && Boolean(props.formik.errors.ResidenceCity)}
            helperText={props.formik.touched.ResidenceCity && props.formik.errors.ResidenceCity}
          />
        </Grid>
        <Grid className={classes.formFieldGrid} item xs={12} sm={6} md={8}>
          <TextField
            variant="outlined"
            label={intl.formatMessage({ id: "RESIDENCE_ADDRESS_LABEL" })}
            fullWidth
            autoComplete="address-line1"
            name="ResidenceAddress"
            value={props.formik.values.ResidenceAddress}
            onBlur={props.formik.handleBlur}
            onChange={props.formik.handleChange}
            error={
              props.formik.touched.ResidenceAddress && Boolean(props.formik.errors.ResidenceAddress)
            }
            helperText={
              props.formik.touched.ResidenceAddress && props.formik.errors.ResidenceAddress
            }
          />
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default ContactInformationForm;
